import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ApiResponseError } from '@/api/core/_models';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import CustomerRegistrationConfirmationSkeleton from './components/CustomerRegistrationConfirmationSkeleton';
import { confirmCustomerRegistration, getCustomerRegistration } from './core/_requests';

const EXPIRED_MESSAGE = 'Link expirado. Cadastre-se de novo no terminal.';
const MESSAGE_CLASS = 'fs-2 fw-bold text-gray-900 mb-0';

const isUnknownToken = (error: Error) => error instanceof ApiResponseError && error.status === 404;

// Abrir o link nunca confirma: os apps de SMS e de WhatsApp abrem o link sozinhos para gerar a prévia.
function CustomerRegistrationConfirmationPage() {
  const { token = '' } = useParams<{ token: string }>();
  const registration = useQuery({
    queryKey: ['customer-registration', token],
    queryFn: async () => (await getCustomerRegistration(token)).data,
    // Token desconhecido é 404 de novo na segunda tentativa: repetir só atrasa o "link expirado".
    retry: false,
    meta: { suppressGlobalError: true },
  });
  const confirmation = useMutation({ mutationFn: () => confirmCustomerRegistration(token) });

  const renderContent = () => {
    if (registration.isLoading) return <CustomerRegistrationConfirmationSkeleton />;
    if (registration.isError) {
      return (
        <p className={MESSAGE_CLASS}>
          {isUnknownToken(registration.error) ? EXPIRED_MESSAGE : getErrorMessage(registration.error)}
        </p>
      );
    }
    if (confirmation.isSuccess) return <p className={MESSAGE_CLASS}>Cadastro confirmado. Pode voltar ao terminal.</p>;
    if (confirmation.isError) return <p className={MESSAGE_CLASS}>{getErrorMessage(confirmation.error)}</p>;
    if (!registration.data) return null;
    if (registration.data.status === 'CONFIRMED') return <p className={MESSAGE_CLASS}>Cadastro já confirmado.</p>;
    if (registration.data.status === 'EXPIRED') return <p className={MESSAGE_CLASS}>{EXPIRED_MESSAGE}</p>;

    return (
      <>
        <p className={MESSAGE_CLASS}>Confirmar cadastro de {registration.data.first_name} no EasyPDV?</p>
        <button
          className="btn btn-primary btn-lg w-100"
          disabled={confirmation.isPending}
          type="button"
          onClick={() => confirmation.mutate()}
        >
          {confirmation.isPending ? 'Confirmando...' : 'Confirmar'}
        </button>
      </>
    );
  };

  return (
    <div className="d-flex flex-center min-vh-100 bg-body p-5">
      <div className="card w-100 mw-400px">
        <div aria-live="polite" className="card-body d-flex flex-column gap-8 p-10 text-center">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default CustomerRegistrationConfirmationPage;
